/**
 * error-envelope.ts
 * Single, versioned error envelope shape — KC-2.1 / KC-2.2
 * Lives in contracts-kernel so both backend (kernel) and frontend can import the shape.
 * ZERO Hono / Drizzle / infrastructure imports allowed here.
 */
import { z } from 'zod';

// ---------------------------------------------------------------------------
// Error codes — machine-readable, exhaustive for standard kernel errors
// ---------------------------------------------------------------------------
export const ERROR_CODES = {
  // Generic
  INTERNAL_SERVER_ERROR: 'INTERNAL_SERVER_ERROR',
  NOT_FOUND: 'NOT_FOUND',
  BAD_REQUEST: 'BAD_REQUEST',

  // Validation
  VALIDATION_ERROR: 'VALIDATION_ERROR',

  // Auth — KC-4
  AUTH_TOKEN_MISSING: 'AUTH_TOKEN_MISSING',
  AUTH_TOKEN_MALFORMED: 'AUTH_TOKEN_MALFORMED',
  AUTH_TOKEN_EXPIRED: 'AUTH_TOKEN_EXPIRED',
  AUTH_TOKEN_INVALID: 'AUTH_TOKEN_INVALID',

  // Tenancy — KC-3
  TENANT_MISSING: 'TENANT_MISSING',
  TENANT_CONTEXT_MISSING: 'TENANT_CONTEXT_MISSING',
  TENANT_ID_INVALID: 'TENANT_ID_INVALID',
  ACCESS_DENIED: 'ACCESS_DENIED',

  // Cross-tenant partnership — cross-tenant-kernel-mechanism PR3b
  // No active identity.tenant_partnership grants a runAsPartnerReadTx
  // elevation for this (ownerCompanyId, scope) pair. Fail-closed, never an
  // empty-result fallback.
  PARTNERSHIP_NOT_ACTIVE: 'PARTNERSHIP_NOT_ACTIVE',

  // Record-scoped party guard — cross-tenant-kernel-mechanism PR4. The
  // acting tenant is not the recorded, partnership-backed counterparty for
  // THIS record (any one of the 5 assertCorrectParty conditions is false).
  // No HTTP mapping yet — this guard has no consumer until PR5, which maps
  // it to the same uniform not-found response as "no partnership" per spec
  // ("No Partnership Yields a Uniform Not-Found, Never a Distinguishable
  // Forbidden").
  NOT_THE_COUNTERPARTY: 'NOT_THE_COUNTERPARTY',

  // kernel-cross-tenant-service-write — resolvePartnerClient found 0 or MORE
  // THAN 1 identity.client row for the acting partner tenant under the
  // owner's scope (design.md XT4). 422 at the HTTP boundary — a human data
  // setup call, never guessed or silently disclosed further.
  PARTNER_CLIENT_NOT_RESOLVED: 'PARTNER_CLIENT_NOT_RESOLVED',
  // kernel-cross-tenant-service-write — assertCanCreateAsPartner's create-
  // time party guard (design.md XT2) collapsed to false: actor === owner,
  // no active 'locx.service_order' partnership, or missing the actor's own
  // create capability. 403 at the HTTP boundary — no partial credit, no
  // distinguishable reason surfaced to the caller.
  NOT_AN_AUTHORIZED_PARTNER: 'NOT_AN_AUTHORIZED_PARTNER',

  // Consent lifecycle — cross-tenant-kernel-mechanism PR7. Uniform not-found
  // for a partnership id the caller's tenant is not a participant of (same
  // "no distinguishable forbidden" posture as NOT_FOUND elsewhere).
  PARTNERSHIP_NOT_FOUND: 'PARTNERSHIP_NOT_FOUND',
  // The initiating side attempted to accept its own request — only the
  // non-initiating side may accept (design.md CT1).
  CANNOT_ACCEPT_OWN_PARTNERSHIP_REQUEST: 'CANNOT_ACCEPT_OWN_PARTNERSHIP_REQUEST',
  // A live (non-revoked) partnership already exists for this (owner,
  // counterparty, scope) triple — uq_tenant_partnership_live (migration 0180).
  TENANT_PARTNERSHIP_CONFLICT: 'TENANT_PARTNERSHIP_CONFLICT',

  // Auth — login KI-3
  INVALID_CREDENTIALS: 'INVALID_CREDENTIALS',

  // Role — KI-4
  INSUFFICIENT_ROLE: 'INSUFFICIENT_ROLE',

  // Capability — AZ-3
  INSUFFICIENT_CAPABILITY: 'INSUFFICIENT_CAPABILITY',

  // Domain conflicts
  USER_EMAIL_CONFLICT: 'USER_EMAIL_CONFLICT',
  COMPANY_HAS_ACTIVE_DEPENDENTS: 'COMPANY_HAS_ACTIVE_DEPENDENTS',
  CATALOG_CODE_CONFLICT: 'CATALOG_CODE_CONFLICT',
  // add-kernel-organization-identity — uq_organization_cuit (global, not per-tenant)
  ORGANIZATION_CUIT_CONFLICT: 'ORGANIZATION_CUIT_CONFLICT',
  // add-cuentas-pagar Phase 1 (SUMI-4.3) — uq_supplier_cuit_tenant (migration
  // 0196), PER TENANT on sumi.supplier. Distinct from ORGANIZATION_CUIT_CONFLICT,
  // which is the GLOBAL uniqueness guard on identity.organization.cuit — the
  // same real-world CUIT may legitimately appear as a supplier in two
  // different tenants (SUMI-4's own "same CUIT across two tenants" scenario).
  SUPPLIER_CUIT_CONFLICT: 'SUPPLIER_CUIT_CONFLICT',
  // add-sumi-proveedores-abm — rename-safety guard: a supplier linked to an
  // organization used by more than one tenant cannot be renamed inline
  // (would silently rename what every other tenant sees).
  ORGANIZATION_SHARED_ACROSS_TENANTS: 'ORGANIZATION_SHARED_ACROSS_TENANTS',
  // A tenant admin cannot reset the password of a login that also belongs to
  // another company: the credential is global, so the reset would hand the
  // admin access to tenants they don't administer.
  ACCOUNT_SHARED_ACROSS_TENANTS: 'ACCOUNT_SHARED_ACROSS_TENANTS',
  // kernel-contractor-directory PR1 — the organization-id-keyed partnership
  // route was called against an organization with linkedCompanyId === null
  // (design.md CD5: "not yet a Suite tenant", fail-closed even if the UI is
  // bypassed).
  ORGANIZATION_NOT_A_TENANT: 'ORGANIZATION_NOT_A_TENANT',
  // kernel-contractor-directory PR3 (CD9) — the tenant-triggered CUIT
  // auto-link found >=2 active identity.company rows sharing the same
  // normalized CUIT (no unique index on company.cuit), or the matched
  // organization already anchors a DIFFERENT company than the one the
  // current CUIT match would produce. Fail-closed, never overwrite.
  ORGANIZATION_LINK_CONFLICT: 'ORGANIZATION_LINK_CONFLICT',

  // State machine — KC-6
  INVALID_TRANSITION: 'INVALID_TRANSITION',

  // Tenant admin — KI-15, KI-17
  LAST_ADMIN_CANNOT_BE_REMOVED: 'LAST_ADMIN_CANNOT_BE_REMOVED',

  // Nav config — NAV-7 (platform nav-config write guard)
  NAV_UNKNOWN_MODULE_KEY: 'NAV_UNKNOWN_MODULE_KEY',
  NAV_UNKNOWN_ITEM_KEY: 'NAV_UNKNOWN_ITEM_KEY',

  // File / logo upload — CP-LOGO
  LOGO_MIME_INVALID: 'LOGO_MIME_INVALID',
  LOGO_SIZE_EXCEEDED: 'LOGO_SIZE_EXCEEDED',
  LOGO_STORAGE_NOT_CONFIGURED: 'LOGO_STORAGE_NOT_CONFIGURED',

  // Generic tenant-scoped file upload — SL-62 (add-locx-daily-report-frontend Slice 0)
  FILE_SIZE_EXCEEDED: 'FILE_SIZE_EXCEEDED',

  // Capability catalog + role management — AZ-6 / RM-CAP-6
  /** A capability key supplied in a role create/update body is not in SDD_CAPABILITY_CATALOG. */
  CAPABILITY_NOT_IN_CATALOG: 'CAPABILITY_NOT_IN_CATALOG',
  // nomi-autoservicio-empleado Fase 1 (design D5) — the key IS in the catalog
  // (so CAPABILITY_NOT_IN_CATALOG would be a lie) but is marked
  // `assignable: false` (every self.* key): valid for requireCapability(),
  // rejected when submitted via role create/edit or a per-user grant write.
  /** A capability key is in the catalog but is not assignable via role/grant write (e.g. self.*). */
  CAPABILITY_NOT_ASSIGNABLE: 'CAPABILITY_NOT_ASSIGNABLE',
  /** The target role is a built-in role and cannot be modified or deleted. */
  ROLE_IS_BUILT_IN: 'ROLE_IS_BUILT_IN',
  /** A role with the supplied name already exists for this company. */
  ROLE_NAME_TAKEN: 'ROLE_NAME_TAKEN',
  /** The role is still assigned to a user (or part of a composed role): unassign it before deleting. */
  ROLE_IN_USE: 'ROLE_IN_USE',
} as const;

export type ErrorCode = (typeof ERROR_CODES)[keyof typeof ERROR_CODES];

// ---------------------------------------------------------------------------
// Per-field validation error (populated when code = VALIDATION_ERROR)
// ---------------------------------------------------------------------------
export const FieldErrorSchema = z.object({
  field: z.string(),
  message: z.string(),
  code: z.string().optional(),
});

export type FieldError = z.infer<typeof FieldErrorSchema>;

// ---------------------------------------------------------------------------
// The envelope — KC-2.1 / KC-2.2
// ---------------------------------------------------------------------------
export const ErrorEnvelopeSchema = z.object({
  status: z.number().int(),
  code: z.string(),
  message: z.string(),
  requestId: z.string(),
  errors: z.array(FieldErrorSchema).optional(),
});

export type ErrorEnvelope = z.infer<typeof ErrorEnvelopeSchema>;
